import { useEffect, useState } from "react";
import { C } from "../../lib/colors";
import { fmtMoney } from "../../lib/format";
import { useT } from "../LangContext";
import { useUserPlan } from "../UserPlanContext";
import { mcCall } from "../../lib/mcClient";
import { FIRM_DATABASE } from "../../firmDatabase";

// ─── Fase 14 · Bloque 4 — Strategy Lab ────────────────────────────────────
// Firm-agnostic analytical view of the user's track record. Free for all
// plans (it's the funnel hook). Auto-runs (debounced) whenever the data
// changes and renders four KPI rows: edge, risk, growth, robustness.
export default function StrategyLab({ strategy, onAnalyzed }) {
  const { t } = useT();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isBootstrap = strategy.mode === "bootstrap"
                      && Array.isArray(strategy.bootstrapData)
                      && strategy.bootstrapData.length >= 30
                      && strategy.bootstrapStats;

  useEffect(() => {
    if (!isBootstrap) { setResult(null); return; }
    setLoading(true);
    setError(null);
    let cancelled = false;
    const handle = setTimeout(() => {
      mcCall("analyze", strategy, { seed: 12345, runsPerHorizon: 1500 })
        .then(r => { if (!cancelled) { setResult(r); setLoading(false); onAnalyzed && onAnalyzed(r); } })
        .catch(e => { if (!cancelled) { setError(String(e?.message || e)); setLoading(false); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [strategy, isBootstrap, onAnalyzed]);

  if (!isBootstrap) {
    return (
      <div data-testid="strategy-lab-empty" style={{ marginTop: 16 }}>
        <div className="fg-panel" style={{ padding: 22, color: C.smoke,
                                            fontFamily: "var(--plex)", fontSize: 13,
                                            lineHeight: 1.55, textAlign: "center" }}>
          <div style={{ marginBottom: 10, fontStyle: "italic" }}>{t("lab_empty_title")}</div>
        </div>
      </div>
    );
  }

  if (loading || !result) {
    return (
      <div data-testid="strategy-lab" style={{ marginTop: 16 }}>
        <div className="fg-panel" style={{ padding: 18 }}>
          <div data-testid="strategy-lab-loading"
               style={{ color: C.smoke, fontFamily: "var(--plex)", fontSize: 12 }}>
            <span className="fg-dots" style={{ color: C.brass }}>···</span> {t("lab_computing")}
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return <div style={{ color: C.cinnabar, fontFamily: "var(--plex)", fontSize: 12 }}>⚠ {error}</div>;
  }
  return <LabGrid r={result} />;
}

// 5-card layout: 4 KPI cards in a 2-col grid + 1 full-width compat card.
function LabGrid({ r }) {
  const { t } = useT();
  const fmt = (v, d = 0) => v == null ? "—" : Number(v).toFixed(d);
  const fmtP = (p) => `${(p * 100).toFixed(1)}%`;
  const sig = r.pValue < 0.05;
  const sigText = sig ? t("lab_edge_real", { p: r.pValue.toFixed(3) })
                       : t("lab_edge_luck", { n: r.n, p: r.pValue.toFixed(3) });
  const sigColor = sig ? C.bone : C.cinnabar;
  const sqnTier = r.sqn < 1.6 ? "poor" : r.sqn < 2 ? "average" : r.sqn < 3 ? "good" : "excellent";

  return (
    <div data-testid="strategy-lab" style={{ marginTop: 16 }}>
      <div data-testid="lab-body"
           style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                     gap: 14 }}>
        {/* Card 1 — Edge */}
        <LabCard kicker="04A" title={t("lab_section_edge")} testid="lab-card-edge">
          <KPIRow>
            <KPI label={t("lab_expectancy")} val={fmt(r.expectancyDay, 1)} unit="$/d" />
            <KPI label="profit factor" val={r.profitFactor === Infinity ? "∞" : r.profitFactor.toFixed(2)} />
            <KPI label="SQN" val={r.sqn.toFixed(2)} sub={t(`lab_sqn_${sqnTier}`)} tooltip={t("lab_sqn_tooltip")} />
          </KPIRow>
          <KPIRow>
            <KPI label={t("lab_significance")} val={sig ? "✓" : "⚠"}
                 valColor={sigColor} sub={sigText} wide />
          </KPIRow>
        </LabCard>

        {/* Card 2 — Risk */}
        <LabCard kicker="04B" title={t("lab_section_risk")} testid="lab-card-risk">
          <KPIRow>
            <KPI label="max DD 30d" val={`${fmtMoney(Math.round(r.maxDD.d30.p50))} / ${fmtMoney(Math.round(r.maxDD.d30.p90))}`} sub="p50 · p90" />
            <KPI label="max DD 60d" val={`${fmtMoney(Math.round(r.maxDD.d60.p50))} / ${fmtMoney(Math.round(r.maxDD.d60.p90))}`} sub="p50 · p90" />
            <KPI label="max DD 90d" val={`${fmtMoney(Math.round(r.maxDD.d90.p50))} / ${fmtMoney(Math.round(r.maxDD.d90.p90))}`} sub="p50 · p90" />
          </KPIRow>
          <KPIRow>
            <KPI label={t("lab_worst_streak")} val={`${r.worstStreak.p50} / ${r.worstStreak.p90}`}
                 sub={t("lab_streak_sub")} tooltip={t("lab_streak_tooltip")} />
            <KPI label={t("lab_underwater")} val={fmtP(r.timeUnderwaterPct)} sub={t("lab_underwater_sub")} />
          </KPIRow>
        </LabCard>

        {/* Card 3 — Growth */}
        <LabCard kicker="04C" title={t("lab_section_growth")} testid="lab-card-growth">
          <KPIRow>
            <KPI label="Kelly"
                 val={r.kelly > 0 ? `${(r.kelly * 100).toFixed(1)}%` : t("lab_kelly_no_edge")}
                 valColor={r.kelly > 0 ? C.bone : C.cinnabar}
                 sub={r.kelly > 0 ? t("lab_kelly_advice", { f: (r.kelly * 100).toFixed(1) }) : null}
                 tooltip={t("lab_kelly_tooltip")} wide />
          </KPIRow>
        </LabCard>

        {/* Card 4 — Robustness */}
        <LabCard kicker="04D" title={t("lab_section_robust")} testid="lab-card-robust">
          <FlagRow flag={r.splitHalf.flag}
                   textOk={t("lab_split_ok")}
                   textFlag={t("lab_split_flag")} />
          <FlagRow flag={r.concentration.flag}
                   textOk={t("lab_conc_ok", { x: fmtMoney(Math.round(r.concentration.withoutTop3)) })}
                   textFlag={t("lab_conc_flag", { x: fmtMoney(Math.round(r.concentration.withoutTop3)) })} />
        </LabCard>
      </div>

      {/* Card 5 — Compat firms (full width) */}
      <div style={{ marginTop: 14 }}>
        <LabCard kicker="04E" title={t("compat_title")} testid="lab-card-compat">
          <CompatibleFirms maxDD30={r.maxDD.d30} />
        </LabCard>
      </div>
    </div>
  );
}

function LabCard({ kicker, title, testid, children }) {
  return (
    <div data-testid={testid} className="fg-panel" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <span style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10,
                        letterSpacing: 0.22 }}>{kicker} ·</span>
        <span style={{ color: C.bone, fontFamily: "var(--mono)", fontSize: 12.5,
                        letterSpacing: 0.15, textTransform: "uppercase" }}>{title}</span>
      </div>
      <div style={{ display: "grid", gap: 12 }}>{children}</div>
    </div>
  );
}

function LabBody_UNUSED_REMOVED() { return null; }

// ─── Fase 14 · Bloque 7 — Compatible firms list ───────────────────────────
// Compares the user's d30 maxDD distribution (p50 & p90) against each
// plan.ddValue across the firm catalog. Green = ddValue >= p90 (cushion at
// the 90th pct), Amber = ddValue >= p50 (tight), Red = below p50.
function CompatibleFirms({ maxDD30 }) {
  const { t } = useT();
  const { canAccess } = useUserPlan();
  const allowedFirms = (typeof canAccess === "function") ? null : null; // resolve below

  const rows = [];
  for (const firm of FIRM_DATABASE) {
    if (firm.id === "custom") continue;
    for (const plan of firm.plans) {
      let status;
      if (plan.ddValue >= maxDD30.p90) status = "green";
      else if (plan.ddValue >= maxDD30.p50) status = "amber";
      else status = "red";
      rows.push({
        key: plan.planId,
        firmId: firm.id,
        firmName: firm.name,
        planLabel: plan.label,
        ddValue: plan.ddValue,
        status,
        locked: typeof canAccess === "function" ? !canAccess(`firm:${firm.id}`) : false,
      });
    }
  }
  const order = { green: 0, amber: 1, red: 2 };
  rows.sort((a, b) => order[a.status] - order[b.status] || a.ddValue - b.ddValue);

  return (
    <div data-testid="compatible-firms">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                     gap: 8 }}
           title={t("compat_tooltip")}>
        {rows.map(r => <CompatRow key={r.key} {...r} />)}
      </div>
    </div>
  );
}

function CompatRow({ firmId, firmName, planLabel, ddValue, status, locked }) {
  const { t } = useT();
  const palette = {
    green: { dot: "#A8C29E", border: C.dust, label: t("compat_green") },
    amber: { dot: C.brass,   border: C.dust, label: t("compat_amber") },
    red:   { dot: C.cinnabar, border: C.dust, label: t("compat_red") },
  }[status];
  return (
    <div data-testid={`compat-row-${firmId}`}
         style={{ padding: "8px 10px", border: `1px solid ${palette.border}`,
                   background: "rgba(42, 48, 50, 0.30)",
                   display: "flex", alignItems: "center", gap: 10,
                   fontFamily: "var(--plex)", fontSize: 12,
                   opacity: locked ? 0.55 : 1 }}>
      <span style={{ color: palette.dot, fontSize: 14, lineHeight: 1 }}>●</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: C.bone, fontFamily: "var(--mono)", fontSize: 11.5,
                       letterSpacing: 0.12, whiteSpace: "nowrap", overflow: "hidden",
                       textOverflow: "ellipsis" }}>
          {firmName} · {planLabel}
        </div>
        <div style={{ color: palette.dot, fontSize: 10.5, fontStyle: "italic", marginTop: 2 }}>
          {palette.label} · DD {fmtMoney(ddValue)}
        </div>
      </div>
      {locked && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.brass} strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      )}
    </div>
  );
}

function KPIRow({ kicker, testid, children }) {
  return (
    <div data-testid={testid}>
      {kicker && (
        <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 0.22,
                       textTransform: "uppercase", marginBottom: 6 }}>{kicker}</div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {children}
      </div>
    </div>
  );
}

function KPI({ label, val, unit, sub, valColor, tooltip, wide }) {
  return (
    <div style={{ minWidth: wide ? 240 : 130, flex: wide ? "1 1 240px" : "0 1 auto" }}
         title={tooltip || undefined}>
      <div style={{ color: C.steel, fontSize: 10, letterSpacing: 0.18, textTransform: "uppercase",
                     fontFamily: "var(--plex)" }}>
        {label}{tooltip ? <span style={{ marginLeft: 4, color: C.brass }}>ⓘ</span> : null}
      </div>
      <div style={{ color: valColor || C.bone, fontFamily: "var(--mono)", fontSize: 14.5,
                     letterSpacing: 0.18, marginTop: 2 }}>
        {val}{unit ? <span style={{ color: C.smoke, marginLeft: 4, fontSize: 11 }}>{unit}</span> : null}
      </div>
      {sub && (
        <div style={{ color: C.smoke, fontSize: 11, fontStyle: "italic", marginTop: 3,
                       lineHeight: 1.4 }}>{sub}</div>
      )}
    </div>
  );
}

function FlagRow({ flag, textOk, textFlag }) {
  const color = flag ? C.cinnabar : C.bone;
  return (
    <div style={{ color, fontSize: 12, fontFamily: "var(--plex)", lineHeight: 1.5,
                   flex: "1 1 360px", minWidth: 240 }}>
      {flag ? textFlag : textOk}
    </div>
  );
}
